/**
 * AIPromptModal - Modal for entering AI generation prompts
 *
 * Used by slash commands (/ai, /continue) to get user input for AI generation.
 * Displays a simple textarea with submit/cancel buttons.
 *
 * Two modes:
 * - generate: User provides a prompt for new content generation
 * - continue: AI continues writing from cursor position (shows context preview)
 *
 * Usage:
 * <AIPromptModal
 *   isOpen={true}
 *   onSubmit={(prompt) => triggerAIGeneration(prompt)}
 *   onClose={() => setIsOpen(false)}
 *   mode="generate"
 * />
 */

import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * AIPromptModalProps - Configuration for the modal
 *
 * - isOpen: Whether the modal is visible
 * - onSubmit: Called with the user's prompt when they submit
 * - onClose: Called when the modal should close (cancel, escape, etc.)
 * - mode: 'generate' for new content, 'continue' to extend existing text
 * - initialContext: For continue mode, the text before the cursor (last 500 chars)
 */
interface AIPromptModalProps {
  isOpen: boolean;
  onSubmit: (prompt: string) => void;
  onClose: () => void;
  mode: 'generate' | 'continue';
  initialContext?: string; // For continue mode, show preceding text
}

/**
 * AIPromptModal Component
 *
 * A modal dialog that appears when the user invokes /ai or /continue slash commands.
 * Features:
 * - Auto-focuses textarea when opened
 * - Resets prompt when closed
 * - Keyboard shortcuts: Ctrl+Enter to submit, Escape to close
 * - Context preview for continue mode (shows last 200 chars before cursor)
 * - Violet theme to match AI styling throughout the app
 */
export function AIPromptModal({
  isOpen,
  onSubmit,
  onClose,
  mode,
  initialContext,
}: AIPromptModalProps) {
  // Track the user's prompt text
  const [prompt, setPrompt] = useState('');
  // Ref for auto-focus behavior
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus textarea when modal opens
  // This provides better UX - user can immediately start typing
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  // Reset prompt when modal closes
  // This ensures fresh state on next open
  useEffect(() => {
    if (!isOpen) {
      setPrompt('');
    }
  }, [isOpen]);

  // Handle form submission
  // Only submits if prompt has actual content (not just whitespace)
  const handleSubmit = useCallback(() => {
    if (prompt.trim()) {
      onSubmit(prompt.trim());
      onClose();
    }
  }, [prompt, onSubmit, onClose]);

  // Handle keyboard events for shortcuts
  // - Escape: Close the modal
  // - Ctrl+Enter or Cmd+Enter: Submit the form
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        handleSubmit();
      }
    },
    [onClose, handleSubmit]
  );

  // Don't render anything if modal is closed
  if (!isOpen) return null;

  return (
    // Backdrop - covers the entire screen with semi-transparent black
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      {/* Modal container - white card with shadow */}
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-4"
        onKeyDown={handleKeyDown}
      >
        {/* Header with AI icon and title */}
        <div className="flex items-center gap-2 mb-3">
          {/* Violet sparkle icon - matches AI theme */}
          <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
            <svg
              className="w-4 h-4 text-violet-600"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              {/* Sparkle/star shape for AI */}
              <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
            </svg>
          </div>
          {/* Title changes based on mode */}
          <h2 className="text-lg font-semibold">
            {mode === 'generate' ? 'Ask AI to write' : 'Continue writing'}
          </h2>
        </div>

        {/* Context preview for continue mode */}
        {/* Shows the last 200 characters before the cursor so user knows the context */}
        {mode === 'continue' && initialContext && (
          <div className="mb-3 p-2 bg-gray-50 rounded text-sm text-gray-600 max-h-20 overflow-y-auto">
            <span className="text-gray-400">...</span>
            {initialContext.slice(-200)}
          </div>
        )}

        {/* Prompt input textarea */}
        <textarea
          ref={textareaRef}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={
            mode === 'generate'
              ? 'What would you like AI to write? (e.g., "Write a summary of...")'
              : 'What should AI write next?'
          }
          className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
        />

        {/* Keyboard shortcut hint */}
        <p className="text-xs text-gray-400 mt-1 mb-3">
          Press Ctrl+Enter to submit, Esc to cancel
        </p>

        {/* Action buttons */}
        <div className="flex justify-end gap-2">
          {/* Cancel button - text only, no background */}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          {/* Generate button - primary action with violet background */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!prompt.trim()}
            className="px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Generate
          </button>
        </div>
      </div>
    </div>
  );
}
